import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePortalSlug } from "@/contexts/PortalDomainContext";
import api from "@/lib/api";
import { loadTenantFont, resolveLogoUrl } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Star, GoogleLogo, Quotes, ArrowSquareOut, Recycle, UserCircle, ArrowLeft, CaretRight, CaretLeft } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function PortalReferences() {
  const slug = usePortalSlug();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/portal/${slug}/references`).then((r) => {
      setData(r.data);
      const b = r.data.tenant?.branding || {};
      document.documentElement.style.setProperty("--tenant-primary", b.primary_color || "#2A86A8");
      document.documentElement.style.setProperty("--tenant-secondary", b.secondary_color || "#3FA64E");
      document.documentElement.style.setProperty("--tenant-font", `"${b.font_family || "Outfit"}", sans-serif`);
      loadTenantFont(b.font_family);
      document.title = `Referenzen — ${r.data.tenant.name}`;
    }).catch((e) => setErr(e.response?.data?.detail || "Mandant nicht gefunden"));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold mb-2">404</h1>
          <p className="text-zinc-500 mb-4">{err}</p>
          <Link to="/" className="underline text-sm">← Zur Tonneva Startseite</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;

  const { tenant, testimonials = [], google_reviews = [], before_after = [], counts, average_rating } = data;
  const b = tenant.branding || {};
  const primary = b.primary_color || "#2A86A8";
  const secondary = b.secondary_color || "#3FA64E";
  const totalReviews = counts.testimonials + counts.google_reviews;
  const isEmpty = counts.total === 0;

  return (
    <div className="min-h-screen bg-white font-tenant text-tenant-text" style={{ fontFamily: `var(--tenant-font)` }} data-testid="portal-references-page">
      {/* Header — matches PortalHome */}
      <header className="border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={`/portal/${slug}`} className="flex items-center gap-3">
            {b.logo_url ? (
              <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-8" />
            ) : (
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: primary }}>
                <Recycle size={18} weight="bold" className="text-white" />
              </div>
            )}
            <div>
              <div className="font-bold tracking-tight">{tenant.name}</div>
              <div className="text-[10px] text-zinc-500">{tenant.city}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to={`/portal/${slug}`} className="hidden sm:flex text-xs font-medium px-3 py-2 rounded-md hover:bg-zinc-100 text-zinc-600">
              <ArrowLeft size={14} weight="bold" className="mr-1" /> Zurück
            </Link>
            <Link to={`/portal/${slug}/book`}>
              <Button className="rounded-md text-white" style={{ background: primary }} data-testid="ref-header-book-btn">
                Jetzt buchen
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — Trust Badge */}
      <section className="relative overflow-hidden py-16 md:py-20" style={{ background: `linear-gradient(135deg, ${primary}08 0%, ${secondary}08 100%)` }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-4" style={{ background: `${primary}15`, color: primary }}>
            <Star size={12} weight="fill" /> Was Kunden sagen
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
            Sauberkeit, die begeistert.
          </h1>
          {average_rating !== null && average_rating !== undefined && (
            <div className="mt-6 inline-flex items-center gap-3 bg-white border border-black/10 px-5 py-3 rounded-xl shadow-sm" data-testid="avg-rating-badge">
              <div className="text-3xl font-heading font-black" style={{ color: primary }}>{average_rating.toFixed(1)}</div>
              <div>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} size={14} weight="fill" className={n <= Math.round(average_rating) ? "text-amber-400" : "text-zinc-200"} />
                  ))}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">basierend auf {totalReviews} Bewertungen</div>
              </div>
            </div>
          )}
          {isEmpty && (
            <p className="mt-6 text-zinc-500">Bald finden Sie hier echte Kundenstimmen aus Ihrer Region.</p>
          )}
        </div>
      </section>

      {/* Google Reviews */}
      {google_reviews.length > 0 && (
        <section className="py-16 border-t border-black/5" data-testid="google-reviews-section">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHeader
              badge="Google-Rezensionen"
              icon={<GoogleLogo size={12} weight="bold" />}
              title="Verifiziert auf Google"
              subtitle="Alle Rezensionen sind auf unserem Google-Business-Profil einsehbar."
              primary={primary}
            />
            <div className="grid md:grid-cols-3 gap-5">
              {google_reviews.map((r) => (
                <ReviewCard key={r.reference_id} r={r} primary={primary} showGoogleBadge />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-16 border-t border-black/5 bg-zinc-50" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHeader
              badge="Kundenstimmen"
              icon={<Quotes size={12} weight="bold" />}
              title="Direkt von unseren Kunden"
              subtitle="Persönliche Erfahrungen aus der Nachbarschaft."
              primary={primary}
            />
            <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {testimonials.map((r) => (
                <ReviewCard key={r.reference_id} r={r} primary={primary} big />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Before / After */}
      {before_after.length > 0 && (
        <section className="py-16 border-t border-black/5" data-testid="beforeafter-section">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHeader
              badge="Vorher / Nachher"
              icon={<ArrowSquareOut size={12} weight="bold" />}
              title="Sichtbarer Unterschied"
              subtitle="Reale Ergebnisse unserer Reinigungen."
              primary={primary}
            />
            <div className="grid md:grid-cols-2 gap-8">
              {before_after.map((r) => (
                <BeforeAfterCard key={r.reference_id} r={r} primary={primary} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 border-t border-black/5 text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}>
        <div className="max-w-3xl mx-auto px-6 text-white">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Bereit für saubere Tonnen?</h2>
          <p className="mt-4 opacity-90">Testen Sie unseren Service selbst — Termin online in unter 30 Sekunden.</p>
          <div className="mt-8">
            <Link to={`/portal/${slug}/book`}>
              <Button className="rounded-md h-12 px-8 text-base bg-white text-black hover:bg-zinc-100" data-testid="ref-cta-book-btn">
                Adresse prüfen <CaretRight size={14} weight="bold" className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-black/5">
        <div className="max-w-6xl mx-auto px-6 flex justify-between text-xs text-zinc-500">
          <Link to={`/portal/${slug}`} className="hover:underline"><CaretLeft size={11} weight="bold" className="inline" /> Zur Startseite</Link>
          <div className="flex gap-4">
            <Link to={`/portal/${slug}/impressum`} className="hover:underline" data-testid="footer-impressum-link">Impressum</Link>
            <Link to={`/portal/${slug}/agb`} className="hover:underline" data-testid="footer-agb-link">AGB</Link>
            <Link to={`/portal/${slug}/datenschutz`} className="hover:underline" data-testid="footer-datenschutz-link">Datenschutz</Link>
          </div>
          <div>Powered by <span className="font-semibold">Tonneva</span></div>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({ badge, icon, title, subtitle, primary }) {
  return (
    <div className="text-center mb-10">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-3" style={{ background: `${primary}15`, color: primary }}>
        {icon} {badge}
      </div>
      <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-zinc-500 mt-2">{subtitle}</p>}
    </div>
  );
}

function ReviewCard({ r, primary, showGoogleBadge, big }) {
  return (
    <div className={`bg-white ${big ? "p-8" : "p-6"} rounded-xl border border-black/5 shadow-sm hover:shadow-md transition relative`} data-testid={`review-card-${r.reference_id}`}>
      {showGoogleBadge && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-zinc-400">
          <GoogleLogo size={10} weight="bold" /> Google
        </span>
      )}
      <div className="flex gap-0.5 mb-3">
        {[1,2,3,4,5].map((n) => (
          <Star key={n} size={big ? 16 : 13} weight={n <= (r.rating || 0) ? "fill" : "regular"} className={n <= (r.rating || 0) ? "text-amber-400" : "text-zinc-200"} />
        ))}
      </div>
      <p className={`text-zinc-700 leading-relaxed ${big ? "text-lg" : "text-sm"}`}>&ldquo;{r.text}&rdquo;</p>
      <div className="mt-4 flex items-center gap-3">
        {r.avatar_url ? (
          <img src={resolveUrl(r.avatar_url)} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ background: primary }}>
            {(r.author_name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{r.author_name}</div>
          <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
            {r.author_location && <span>{r.author_location}</span>}
            {r.author_location && r.date_iso && <span>·</span>}
            {r.date_iso && <span>{formatDate(r.date_iso)}</span>}
          </div>
        </div>
        {r.source_url && (
          <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-[11px] inline-flex items-center gap-1 flex-shrink-0" title="Original ansehen">
            <ArrowSquareOut size={11} weight="bold" />
          </a>
        )}
      </div>
    </div>
  );
}

function BeforeAfterCard({ r, primary }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="bg-white border border-black/5 rounded-xl overflow-hidden shadow-sm" data-testid={`beforeafter-card-${r.reference_id}`}>
      <div className="relative aspect-video select-none cursor-ew-resize overflow-hidden bg-zinc-100">
        <img src={resolveUrl(r.photo_after_url)} alt="Nachher" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
        <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img src={resolveUrl(r.photo_before_url)} alt="Vorher" className="absolute inset-0 w-full h-full object-cover" style={{ width: `${100 / (pos / 100 || 0.01)}%` }} draggable={false} />
        </div>
        <span className="absolute top-3 left-3 bg-black/70 text-white text-[9px] px-2 py-1 uppercase tracking-widest font-bold">Vorher</span>
        <span className="absolute top-3 right-3 text-white text-[9px] px-2 py-1 uppercase tracking-widest font-bold" style={{ background: primary }}>Nachher</span>
        <div className="absolute inset-y-0 pointer-events-none" style={{ left: `calc(${pos}% - 1px)` }}>
          <div className="w-0.5 h-full bg-white shadow-lg" />
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center" style={{ left: 0 }}>
            <CaretLeft size={10} weight="bold" />
            <CaretRight size={10} weight="bold" />
          </div>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={pos}
          onChange={(e) => setPos(+e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          data-testid={`slider-${r.reference_id}`}
          aria-label="Vorher / Nachher Vergleich"
        />
      </div>
      {r.caption && <div className="p-4 text-sm text-zinc-600">{r.caption}</div>}
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}
